import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUpload,
  faFileImport,
  faShieldAlt,
  faExclamationTriangle,
  faCheckCircle,
  faTimesCircle,
  faSpinner,
  faFilter,
  faChevronDown,
  faChevronUp,
  faExternalLinkAlt,
  faHistory,
  faTrash,
  faBug,
  faServer,
  faNetworkWired
} from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import { useTheme } from '../contexts/ThemeContext';
import PageHeader from '../components/common/PageHeader';
import { useBranding } from '../contexts/BrandingContext';
import { useLanguage } from '../contexts/LanguageContext';
import axios from '../config/axiosConfig';

const VulnScanImport = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { darkMode } = useTheme();
  const { primaryColor } = useBranding();
  const { t } = useLanguage();
  const fileInputRef = useRef(null);

  // State
  const [step, setStep] = useState('upload'); // upload, preview, importing, complete
  const [uploading, setUploading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importData, setImportData] = useState(null);
  const [importId, setImportId] = useState(null);
  const [findings, setFindings] = useState([]);
  const [attackSummary, setAttackSummary] = useState(null);
  const [importHistory, setImportHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [supportedFormats, setSupportedFormats] = useState([]);

  // Filters and options
  const [filters, setFilters] = useState({
    severity: 'all',
    mapped: 'all',
    search: ''
  });
  const [importOptions, setImportOptions] = useState({
    createActions: true,
    createBlueTeamFindings: false,
    linkToTargets: true,
    minSeverity: 'low',
    onlyMapped: false,
    deduplicateByCVE: true
  });
  const [expandedFindings, setExpandedFindings] = useState(new Set());

  // Load supported formats and import history
  useEffect(() => {
    fetchSupportedFormats();
    if (projectId) {
      fetchImportHistory();
    }
  }, [projectId]);

  const fetchSupportedFormats = async () => {
    try {
      const response = await axios.get('/vulnscan/supported-formats');
      setSupportedFormats(response.data.formats || []);
    } catch (error) {
      console.error('Error fetching supported formats:', error);
    }
  };

  const fetchImportHistory = async () => {
    try {
      const response = await axios.get(`/vulnscan/history/${projectId}`);
      setImportHistory(response.data);
    } catch (error) {
      console.error('Error fetching import history:', error);
    }
  };

  const handleFileSelect = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploading(true);

    try {
      const formData = new FormData();
      formData.append('scanFile', file);

      const response = await axios.post(`/vulnscan/upload/${projectId}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setImportData(response.data);
      setImportId(response.data.importId);
      setAttackSummary(response.data.attackSummary);

      // Fetch full findings list
      const previewResponse = await axios.get(`/vulnscan/preview/${response.data.importId}`);
      setFindings(previewResponse.data.import.findings || []);

      setStep('preview');
      toast.success(`Parsed ${response.data.stats.totalFindings} findings from ${file.name}`);
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.response?.data?.message || 'Failed to parse scan file');
    } finally {
      setUploading(false);
    }
  };

  const handleToggleFinding = (index) => {
    const newFindings = [...findings];
    newFindings[index].selected = !newFindings[index].selected;
    setFindings(newFindings);
  };

  const handleSelectAll = (selected) => {
    setFindings(findings.map(f => ({ ...f, selected })));
  };

  const handleSelectFiltered = (selected) => {
    const filteredIndices = getFilteredFindings().map(f => findings.indexOf(f));
    setFindings(findings.map((f, idx) => ({
      ...f,
      selected: filteredIndices.includes(idx) ? selected : f.selected
    })));
  };

  const handleConfirmImport = async () => {
    setImporting(true);
    setStep('importing');

    try {
      // Update selections on server
      const selections = findings.reduce((acc, f, idx) => {
        acc[idx] = f.selected;
        return acc;
      }, {});

      await axios.put(`/vulnscan/preview/${importId}/selection`, {
        selections,
        importOptions
      });

      // Confirm import
      const response = await axios.post(`/vulnscan/confirm/${importId}`);

      setStep('complete');
      toast.success(`Import complete: ${response.data.results.actionsCreated} actions created`);

      // Refresh history
      fetchImportHistory();
    } catch (error) {
      console.error('Import error:', error);
      toast.error(error.response?.data?.message || 'Import failed');
      setStep('preview');
    } finally {
      setImporting(false);
    }
  };

  const handleDeleteImport = async (importIdToDelete) => {
    if (!window.confirm('Delete this import record? This will not delete created actions.')) return;

    try {
      await axios.delete(`/vulnscan/${importIdToDelete}`);
      toast.success('Import record deleted');
      fetchImportHistory();
    } catch (error) {
      toast.error('Failed to delete import');
    }
  };

  const getFilteredFindings = () => {
    return findings.filter(f => {
      if (filters.severity !== 'all' && f.severity !== filters.severity) return false;
      if (filters.mapped === 'mapped' && (!f.attackMappings || f.attackMappings.length === 0)) return false;
      if (filters.mapped === 'unmapped' && f.attackMappings && f.attackMappings.length > 0) return false;
      if (filters.search) {
        const searchLower = filters.search.toLowerCase();
        const matchesSearch =
          f.pluginName?.toLowerCase().includes(searchLower) ||
          f.cve?.some(c => c.toLowerCase().includes(searchLower)) ||
          f.host?.toLowerCase().includes(searchLower) ||
          f.description?.toLowerCase().includes(searchLower);
        if (!matchesSearch) return false;
      }
      return true;
    });
  };

  const getSeverityColor = (severity) => {
    const colors = {
      critical: 'bg-red-600 text-white',
      high: 'bg-orange-500 text-white',
      medium: 'bg-yellow-500 text-black',
      low: 'bg-blue-500 text-white',
      info: 'bg-gray-500 text-white'
    };
    return colors[severity] || colors.info;
  };

  const getSeverityBadge = (severity) => {
    return (
      <span className={`px-2 py-0.5 rounded text-xs font-medium ${getSeverityColor(severity)}`}>
        {severity?.toUpperCase()}
      </span>
    );
  };

  const toggleExpanded = (index) => {
    const newExpanded = new Set(expandedFindings);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedFindings(newExpanded);
  };

  const selectedCount = findings.filter(f => f.selected).length;
  const filteredFindings = getFilteredFindings();

  // Render upload step
  const renderUploadStep = () => (
    <div className="space-y-6">
      {/* Upload Area */}
      <div
        onClick={() => fileInputRef.current?.click()}
        className={`border-2 border-dashed rounded-lg p-12 text-center cursor-pointer transition-colors ${
          darkMode
            ? 'border-gray-600 hover:border-gray-500 bg-gray-800/50'
            : 'border-gray-300 hover:border-gray-400 bg-gray-50'
        }`}
      >
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileSelect}
          accept=".nessus,.xml,.json"
          className="hidden"
        />
        {uploading ? (
          <div className="flex flex-col items-center">
            <FontAwesomeIcon icon={faSpinner} spin className="text-4xl mb-4" style={{ color: primaryColor }} />
            <p className={darkMode ? 'text-gray-300' : 'text-gray-600'}>Parsing scan file...</p>
          </div>
        ) : (
          <>
            <FontAwesomeIcon icon={faUpload} className="text-4xl mb-4" style={{ color: primaryColor }} />
            <p className={`text-lg font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              Drop your vulnerability scan file here
            </p>
            <p className={`mt-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              or click to browse
            </p>
            <p className={`mt-4 text-sm ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
              Supported: .nessus, .xml (Qualys, OpenVAS), .json
            </p>
          </>
        )}
      </div>

      {/* Supported Formats */}
      <div className={`rounded-lg p-4 ${darkMode ? 'bg-gray-800' : 'bg-white'} border ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
        <h3 className={`font-medium mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
          Supported Vulnerability Scanners
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {supportedFormats.map(format => (
            <div key={format.id} className={`p-3 rounded ${darkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
              <div className="flex items-center space-x-2">
                <FontAwesomeIcon icon={faBug} style={{ color: primaryColor }} />
                <span className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  {format.name}
                </span>
              </div>
              <p className={`text-xs mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                {format.extensions.join(', ')}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Import History */}
      {importHistory.length > 0 && (
        <div className={`rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-white'} border ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
          <button
            onClick={() => setShowHistory(!showHistory)}
            className={`w-full flex items-center justify-between p-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}
          >
            <div className="flex items-center space-x-2">
              <FontAwesomeIcon icon={faHistory} />
              <span className="font-medium">Import History ({importHistory.length})</span>
            </div>
            <FontAwesomeIcon icon={showHistory ? faChevronUp : faChevronDown} />
          </button>
          {showHistory && (
            <div className="border-t border-gray-700 divide-y divide-gray-700">
              {importHistory.map(imp => (
                <div key={imp._id} className="p-4 flex items-center justify-between">
                  <div>
                    <p className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                      {imp.filename}
                    </p>
                    <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      {new Date(imp.importedAt).toLocaleString()} • {imp.scannerType} •
                      {imp.stats?.actionsCreated || 0} actions created
                    </p>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className={`px-2 py-1 rounded text-xs ${
                      imp.status === 'completed' ? 'bg-green-500/20 text-green-500' :
                      imp.status === 'partial' ? 'bg-yellow-500/20 text-yellow-500' :
                      'bg-gray-500/20 text-gray-500'
                    }`}>
                      {imp.status}
                    </span>
                    <button
                      onClick={() => handleDeleteImport(imp._id)}
                      className="p-1 text-red-500 hover:text-red-400"
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );

  // Render preview step
  const renderPreviewStep = () => (
    <div className="space-y-6">
      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
        <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-white'} border ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
          <p className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            {importData?.stats?.totalFindings || 0}
          </p>
          <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Total Findings</p>
        </div>
        <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-white'} border border-red-500/30`}>
          <p className="text-2xl font-bold text-red-500">{importData?.stats?.criticalCount || 0}</p>
          <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Critical</p>
        </div>
        <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-white'} border border-orange-500/30`}>
          <p className="text-2xl font-bold text-orange-500">{importData?.stats?.highCount || 0}</p>
          <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>High</p>
        </div>
        <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-white'} border border-yellow-500/30`}>
          <p className="text-2xl font-bold text-yellow-500">{importData?.stats?.mediumCount || 0}</p>
          <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Medium</p>
        </div>
        <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-white'} border ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
          <p className="text-2xl font-bold" style={{ color: primaryColor }}>
            {importData?.stats?.mappedToAttack || 0}
          </p>
          <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>ATT&CK Mapped</p>
        </div>
        <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-white'} border ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
          <p className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            {importData?.stats?.hostsScanned || 0}
          </p>
          <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Hosts</p>
        </div>
      </div>

      {/* ATT&CK Mapping Summary */}
      {attackSummary && Object.keys(attackSummary.byTactic).length > 0 && (
        <div className={`rounded-lg p-4 ${darkMode ? 'bg-gray-800' : 'bg-white'} border ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
          <h3 className={`font-medium mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            <FontAwesomeIcon icon={faShieldAlt} className="mr-2" style={{ color: primaryColor }} />
            MITRE ATT&CK Mapping Preview
          </h3>
          <div className="flex flex-wrap gap-2">
            {Object.entries(attackSummary.byTactic).map(([tactic, data]) => (
              <div key={tactic} className={`px-3 py-2 rounded ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                <span className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  {tactic}
                </span>
                <span className="ml-2 text-sm" style={{ color: primaryColor }}>
                  {Object.keys(data.techniques).length} techniques
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Import Options */}
      <div className={`rounded-lg p-4 ${darkMode ? 'bg-gray-800' : 'bg-white'} border ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
        <h3 className={`font-medium mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>Import Options</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <label className="flex items-center space-x-2 cursor-pointer">
            <input
              type="checkbox"
              checked={importOptions.createActions}
              onChange={e => setImportOptions({ ...importOptions, createActions: e.target.checked })}
              className="rounded"
            />
            <span className={darkMode ? 'text-gray-300' : 'text-gray-700'}>Create Actions (Red Team)</span>
          </label>
          <label className="flex items-center space-x-2 cursor-pointer">
            <input
              type="checkbox"
              checked={importOptions.createBlueTeamFindings}
              onChange={e => setImportOptions({ ...importOptions, createBlueTeamFindings: e.target.checked })}
              className="rounded"
            />
            <span className={darkMode ? 'text-gray-300' : 'text-gray-700'}>Create Blue Team Findings</span>
          </label>
          <label className="flex items-center space-x-2 cursor-pointer">
            <input
              type="checkbox"
              checked={importOptions.linkToTargets}
              onChange={e => setImportOptions({ ...importOptions, linkToTargets: e.target.checked })}
              className="rounded"
            />
            <span className={darkMode ? 'text-gray-300' : 'text-gray-700'}>Create Targets from Hosts</span>
          </label>
          <label className="flex items-center space-x-2 cursor-pointer">
            <input
              type="checkbox"
              checked={importOptions.deduplicateByCVE}
              onChange={e => setImportOptions({ ...importOptions, deduplicateByCVE: e.target.checked })}
              className="rounded"
            />
            <span className={darkMode ? 'text-gray-300' : 'text-gray-700'}>Deduplicate by CVE</span>
          </label>
          <label className="flex items-center space-x-2 cursor-pointer">
            <input
              type="checkbox"
              checked={importOptions.onlyMapped}
              onChange={e => setImportOptions({ ...importOptions, onlyMapped: e.target.checked })}
              className="rounded"
            />
            <span className={darkMode ? 'text-gray-300' : 'text-gray-700'}>Only ATT&CK Mapped</span>
          </label>
          <div className="flex items-center space-x-2">
            <span className={darkMode ? 'text-gray-300' : 'text-gray-700'}>Min Severity:</span>
            <select
              value={importOptions.minSeverity}
              onChange={e => setImportOptions({ ...importOptions, minSeverity: e.target.value })}
              className={`rounded px-2 py-1 text-sm ${darkMode ? 'bg-gray-700 text-white' : 'bg-gray-100 text-gray-900'}`}
            >
              <option value="info">Info</option>
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
              <option value="critical">Critical</option>
            </select>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className={`rounded-lg p-4 ${darkMode ? 'bg-gray-800' : 'bg-white'} border ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center space-x-2">
            <FontAwesomeIcon icon={faFilter} className={darkMode ? 'text-gray-400' : 'text-gray-500'} />
            <span className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>Filter:</span>
          </div>
          <select
            value={filters.severity}
            onChange={e => setFilters({ ...filters, severity: e.target.value })}
            className={`rounded px-3 py-1.5 text-sm ${darkMode ? 'bg-gray-700 text-white' : 'bg-gray-100 text-gray-900'}`}
          >
            <option value="all">All Severities</option>
            <option value="critical">Critical</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
            <option value="info">Info</option>
          </select>
          <select
            value={filters.mapped}
            onChange={e => setFilters({ ...filters, mapped: e.target.value })}
            className={`rounded px-3 py-1.5 text-sm ${darkMode ? 'bg-gray-700 text-white' : 'bg-gray-100 text-gray-900'}`}
          >
            <option value="all">All Mappings</option>
            <option value="mapped">ATT&CK Mapped</option>
            <option value="unmapped">Not Mapped</option>
          </select>
          <input
            type="text"
            placeholder="Search CVE, host, name..."
            value={filters.search}
            onChange={e => setFilters({ ...filters, search: e.target.value })}
            className={`rounded px-3 py-1.5 text-sm flex-1 min-w-[200px] ${darkMode ? 'bg-gray-700 text-white placeholder-gray-400' : 'bg-gray-100 text-gray-900'}`}
          />
          <div className="flex items-center space-x-2">
            <button
              onClick={() => handleSelectFiltered(true)}
              className="px-3 py-1.5 text-sm rounded hover:opacity-80"
              style={{ backgroundColor: primaryColor, color: 'white' }}
            >
              Select Filtered
            </button>
            <button
              onClick={() => handleSelectFiltered(false)}
              className={`px-3 py-1.5 text-sm rounded ${darkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-200 hover:bg-gray-300'}`}
            >
              Deselect Filtered
            </button>
          </div>
        </div>
        <div className={`mt-2 text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          Showing {filteredFindings.length} of {findings.length} findings • {selectedCount} selected for import
        </div>
      </div>

      {/* Findings List */}
      <div className={`rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-white'} border ${darkMode ? 'border-gray-700' : 'border-gray-200'} overflow-hidden`}>
        <div className={`p-3 border-b ${darkMode ? 'border-gray-700 bg-gray-700/50' : 'border-gray-200 bg-gray-50'} flex items-center justify-between`}>
          <div className="flex items-center space-x-3">
            <input
              type="checkbox"
              checked={findings.length > 0 && findings.every(f => f.selected)}
              onChange={e => handleSelectAll(e.target.checked)}
              className="rounded"
            />
            <span className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              Select All
            </span>
          </div>
          <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            {selectedCount} / {findings.length} selected
          </span>
        </div>
        <div className="max-h-[500px] overflow-y-auto divide-y divide-gray-700">
          {filteredFindings.slice(0, 100).map((finding, idx) => {
            const actualIndex = findings.indexOf(finding);
            const isExpanded = expandedFindings.has(actualIndex);

            return (
              <div key={actualIndex} className={`${darkMode ? 'hover:bg-gray-700/50' : 'hover:bg-gray-50'}`}>
                <div className="p-3 flex items-start space-x-3">
                  <input
                    type="checkbox"
                    checked={finding.selected}
                    onChange={() => handleToggleFinding(actualIndex)}
                    className="mt-1 rounded"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 flex-wrap">
                          {getSeverityBadge(finding.severity)}
                          <span className={`font-medium truncate ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                            {finding.pluginName}
                          </span>
                        </div>
                        <div className={`flex items-center space-x-3 mt-1 text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          <span className="flex items-center">
                            <FontAwesomeIcon icon={faServer} className="mr-1" />
                            {finding.host}
                            {finding.port > 0 && `:${finding.port}`}
                          </span>
                          {finding.cve && finding.cve.length > 0 && (
                            <span className="text-red-400">{finding.cve.join(', ')}</span>
                          )}
                        </div>
                        {finding.attackMappings && finding.attackMappings.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-2">
                            {finding.attackMappings.map((mapping, mIdx) => (
                              <span
                                key={mIdx}
                                className="px-2 py-0.5 rounded text-xs"
                                style={{ backgroundColor: `${primaryColor}20`, color: primaryColor }}
                              >
                                {mapping.techniqueId}: {mapping.techniqueName}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                      <button
                        onClick={() => toggleExpanded(actualIndex)}
                        className={`p-1 ${darkMode ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-900'}`}
                      >
                        <FontAwesomeIcon icon={isExpanded ? faChevronUp : faChevronDown} />
                      </button>
                    </div>
                    {isExpanded && (
                      <div className={`mt-3 p-3 rounded ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                        {finding.synopsis && (
                          <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                            <strong>Synopsis:</strong> {finding.synopsis}
                          </p>
                        )}
                        {finding.description && (
                          <p className={`text-sm mt-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                            <strong>Description:</strong> {finding.description.substring(0, 500)}
                            {finding.description.length > 500 && '...'}
                          </p>
                        )}
                        {finding.solution && (
                          <p className={`text-sm mt-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                            <strong>Solution:</strong> {finding.solution.substring(0, 300)}
                          </p>
                        )}
                        {finding.exploitAvailable && (
                          <p className="text-sm mt-2 text-red-400">
                            <FontAwesomeIcon icon={faExclamationTriangle} className="mr-1" />
                            Exploit Available
                            {finding.exploitFrameworks?.length > 0 && ` (${finding.exploitFrameworks.join(', ')})`}
                          </p>
                        )}
                        {finding.cvss3Score && (
                          <p className={`text-sm mt-2 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                            CVSS 3.x: {finding.cvss3Score} {finding.cvss3Vector && `(${finding.cvss3Vector})`}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
          {filteredFindings.length > 100 && (
            <div className={`p-4 text-center ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Showing first 100 of {filteredFindings.length} findings. Use filters to narrow down.
            </div>
          )}
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex items-center justify-between">
        <button
          onClick={() => {
            setStep('upload');
            setFindings([]);
            setImportData(null);
            setImportId(null);
          }}
          className={`px-4 py-2 rounded ${darkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-200 hover:bg-gray-300'}`}
        >
          Cancel
        </button>
        <button
          onClick={handleConfirmImport}
          disabled={selectedCount === 0}
          className="px-6 py-2 rounded text-white font-medium hover:opacity-90 disabled:opacity-50"
          style={{ backgroundColor: primaryColor }}
        >
          Import {selectedCount} Selected Findings
        </button>
      </div>
    </div>
  );

  // Render importing step
  const renderImportingStep = () => (
    <div className="flex flex-col items-center justify-center py-16">
      <FontAwesomeIcon icon={faSpinner} spin className="text-5xl mb-6" style={{ color: primaryColor }} />
      <h3 className={`text-xl font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
        Importing Findings...
      </h3>
      <p className={`mt-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
        Creating actions and linking to MITRE ATT&CK techniques
      </p>
    </div>
  );

  // Render complete step
  const renderCompleteStep = () => (
    <div className="flex flex-col items-center justify-center py-16">
      <FontAwesomeIcon icon={faCheckCircle} className="text-5xl mb-6 text-green-500" />
      <h3 className={`text-xl font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
        Import Complete!
      </h3>
      <p className={`mt-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
        Vulnerability findings have been imported and mapped to MITRE ATT&CK techniques.
      </p>
      <div className="flex items-center space-x-4 mt-6">
        <button
          onClick={() => navigate(`/projects/${projectId}`)}
          className="px-4 py-2 rounded text-white hover:opacity-90"
          style={{ backgroundColor: primaryColor }}
        >
          View Project
        </button>
        <button
          onClick={() => {
            setStep('upload');
            setFindings([]);
            setImportData(null);
            setImportId(null);
          }}
          className={`px-4 py-2 rounded ${darkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-200 hover:bg-gray-300'}`}
        >
          Import Another
        </button>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <PageHeader
        title="Import Vulnerability Scan"
        subtitle="Import findings from Nessus, Qualys, OpenVAS and map to MITRE ATT&CK"
        icon={<FontAwesomeIcon icon={faFileImport} style={{ color: primaryColor }} />}
      />

      {/* Progress Steps */}
      <div className="flex items-center justify-center space-x-4">
        {['upload', 'preview', 'importing', 'complete'].map((s, idx) => (
          <React.Fragment key={s}>
            {idx > 0 && (
              <div className={`w-12 h-0.5 ${step === s || ['preview', 'importing', 'complete'].indexOf(step) > idx - 1 ? '' : darkMode ? 'bg-gray-700' : 'bg-gray-300'}`}
                   style={step === s || ['upload', 'preview', 'importing', 'complete'].indexOf(step) >= idx ? { backgroundColor: primaryColor } : {}} />
            )}
            <div className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium ${
              step === s ? 'text-white' : darkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-200 text-gray-500'
            }`} style={step === s || ['upload', 'preview', 'importing', 'complete'].indexOf(step) >= idx ? { backgroundColor: primaryColor, color: 'white' } : {}}>
              {idx + 1}
            </div>
          </React.Fragment>
        ))}
      </div>

      {/* Step Content */}
      {step === 'upload' && renderUploadStep()}
      {step === 'preview' && renderPreviewStep()}
      {step === 'importing' && renderImportingStep()}
      {step === 'complete' && renderCompleteStep()}
    </div>
  );
};

export default VulnScanImport;
